/**
 * MealPrescriptionModal — modal com sugestões de refeições do Spirit.
 * Fetches /api/programs/{program}/macros/prescription and displays meals.
 *
 * Props:
 *   open: boolean
 *   program: string (e.g. "keto_fusion")
 *   onClose: callback
 *   onSelect: (meal) => void — quando utilizador escolhe uma refeição
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Beef, Wheat, Droplet, Flame, Check, ChevronDown, ChefHat } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function MealPrescriptionModal({ open, program = "keto_fusion", onClose, onSelect }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API}/api/programs/${program}/macros/prescription`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ meals: [], error: true }))
      .finally(() => setLoading(false));
  }, [open, program]);

  if (!open) return null;
  const remaining = data?.remaining;
  const meals = data?.meals || [];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
        data-testid="meal-prescription-modal"
      >
        <motion.div
          initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
          className="bg-zinc-950 border border-emerald-500/30 rounded-3xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          style={{ boxShadow: "0 20px 80px rgba(0,255,136,0.15)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <motion.div animate={{ rotate: [0, 8, -8, 0] }} transition={{ duration: 4, repeat: Infinity }}>
                <Sparkles className="w-5 h-5 text-emerald-400" />
              </motion.div>
              <h3 className="text-lg font-bold text-zinc-100">Spirit sugere para fechar hoje</h3>
            </div>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="rx-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Remaining macros summary */}
          {remaining && (
            <div className="p-3 rounded-xl bg-zinc-900/60 border border-zinc-800 mb-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Ainda em falta</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div><Flame className="w-3.5 h-3.5 text-emerald-400 mx-auto mb-1" /><p className="text-xs font-mono text-emerald-300">{Math.round(remaining.kcal)}<span className="text-zinc-500 text-[10px]">kcal</span></p></div>
                <div><Wheat className="w-3.5 h-3.5 text-lime-400 mx-auto mb-1" /><p className="text-xs font-mono text-lime-300">{Math.round(remaining.carbs_g)}<span className="text-zinc-500 text-[10px]">g C</span></p></div>
                <div><Beef className="w-3.5 h-3.5 text-orange-400 mx-auto mb-1" /><p className="text-xs font-mono text-orange-300">{Math.round(remaining.protein_g)}<span className="text-zinc-500 text-[10px]">g P</span></p></div>
                <div><Droplet className="w-3.5 h-3.5 text-purple-400 mx-auto mb-1" /><p className="text-xs font-mono text-purple-300">{Math.round(remaining.fat_g)}<span className="text-zinc-500 text-[10px]">g F</span></p></div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-10 text-center">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }} className="inline-block">
                <Sparkles className="w-8 h-8 text-emerald-400" />
              </motion.div>
              <p className="text-xs text-zinc-500 mt-2">Spirit a pensar...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {meals.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
                  className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/40 hover:border-emerald-500/40 transition-all"
                  data-testid={`meal-rx-${i}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="text-sm font-bold text-zinc-100 flex-1">{m.name}</h4>
                    {m.source === "spirit" && (
                      <span className="text-[9px] uppercase tracking-widest text-emerald-400 px-1.5 py-0.5 rounded bg-emerald-500/10">AI</span>
                    )}
                  </div>
                  {m.why_pt && <p className="text-[11px] text-zinc-400 italic mb-2 leading-snug">{m.why_pt}</p>}
                  <div className="flex items-center gap-3 text-[11px] font-mono mb-3">
                    <span className="text-emerald-300">{m.kcal} kcal</span>
                    <span className="text-lime-300">C{m.carbs_g}</span>
                    <span className="text-orange-300">P{m.protein_g}</span>
                    <span className="text-purple-300">F{m.fat_g}</span>
                  </div>

                  {/* Recipe expand toggle */}
                  {(m.ingredients_pt?.length || m.prep_pt) && (
                    <button
                      onClick={() => setExpanded(expanded === i ? null : i)}
                      className="w-full flex items-center justify-between text-[11px] text-emerald-400 hover:text-emerald-300 py-1.5 mb-2 border-t border-zinc-800/60 pt-2"
                      data-testid={`meal-rx-expand-${i}`}
                    >
                      <span className="flex items-center gap-1">
                        <ChefHat className="w-3 h-3" /> Receita + modo de preparo
                      </span>
                      <motion.div animate={{ rotate: expanded === i ? 180 : 0 }}>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </motion.div>
                    </button>
                  )}

                  <AnimatePresence>
                    {expanded === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden mb-3"
                      >
                        {m.ingredients_pt?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Ingredientes</p>
                            <ul className="space-y-0.5">
                              {m.ingredients_pt.map((ing, k) => (
                                <li key={k} className="text-[11px] text-zinc-300 flex items-start gap-1.5">
                                  <span className="text-emerald-400 mt-0.5">•</span>
                                  <span>{ing}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {m.prep_pt && (
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Modo de preparo</p>
                            <p className="text-[11px] text-zinc-300 leading-relaxed italic">{m.prep_pt}</p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Button
                    onClick={() => onSelect?.(m)}
                    className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 h-8 text-xs"
                    data-testid={`meal-rx-select-${i}`}
                  >
                    <Check className="w-3 h-3 mr-1" /> Registar como comido
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
